import React, { useEffect, useState } from 'react';

import { Autocomplete, createFilterOptions, FilterOptionsState, Popper } from '@mui/material';
import { Message } from 'react-hook-form';

import { Checkbox, CheckboxType } from 'components/Checkbox';
import { DeleteItemIcon } from 'assets/icons';
import { SelectArrow } from 'assets/images';
import useMobileCheck from 'hooks/useMobileCheck';
import useTabletCheck from 'hooks/useTabletCheck';
import InputError from 'components/Inputs/InputError';

import styles from './MultipleSelect.module.scss';
import { AutoCompleteInput, AutoCompleteLabel } from './StyledComponents';
import {
  DEFAULT_LIMIT_TAGS_VALUE,
  DEFAULT_SELECT_ALL_OPTION_LABEL,
  DEFAULT_SELECT_ALL_OPTION_VALUE,
} from './multipleSelect.constants';

export interface MultipleSelectOption {
  label: string;
  value: string;
}

interface MultipleSelectProps {
  id: string;
  selectOptions: MultipleSelectOption[];
  inputLabel: string;
  useSelectAll: boolean;
  onChange: (value: MultipleSelectOption[]) => void;
  limitTags?: number;
  selectAllLabel?: string;
  errorMessage?: Message;
  initialValues?: MultipleSelectOption[];
}

const MultipleSelect = ({
  id,
  selectOptions,
  onChange,
  useSelectAll,
  inputLabel,
  errorMessage,
  selectAllLabel = DEFAULT_SELECT_ALL_OPTION_LABEL,
  limitTags = DEFAULT_LIMIT_TAGS_VALUE,
  initialValues = [],
}: MultipleSelectProps) => {
  const isMobile = useMobileCheck();
  const isTablet = useTabletCheck();

  const [currentValues, setCurrentValues] = useState<MultipleSelectOption[]>(initialValues);

  useEffect(() => {
    currentValues.forEach((value) => {
      if (!selectOptions.find((option) => option.label === value.label)) {
        setCurrentValues([]);
      }
    });
  }, [currentValues, selectOptions]);

  return (
    <div className={styles.mainContainer}>
      <Autocomplete
        id={id}
        multiple
        options={selectOptions}
        value={currentValues}
        popupIcon={<SelectArrow />}
        disableCloseOnSelect
        getOptionLabel={(option) => option.label}
        PopperComponent={({ style, ...props }) => <Popper {...props} style={{ ...style, height: 0 }} />}
        filterOptions={(options, params) => {
          const filter = createFilterOptions();
          const filteredOptions = filter(options, params as FilterOptionsState<unknown>);

          if (useSelectAll) {
            return [
              { label: selectAllLabel, value: DEFAULT_SELECT_ALL_OPTION_VALUE },
              ...filteredOptions,
            ] as unknown as MultipleSelectOption[];
          } else {
            return filteredOptions as unknown as MultipleSelectOption[];
          }
        }}
        renderTags={(options, getTagProps) => (
          <>
            {isTablet ? (
              <div>{`${options.length} Items Selected`}</div>
            ) : (
              <>
                {options.slice(0, limitTags).map((option, index) => (
                  <AutoCompleteLabel label={option.label} deleteIcon={<DeleteItemIcon />} {...getTagProps({ index })} />
                ))}
                <div>{options.length > limitTags && ` +${options.length - limitTags}`}</div>
              </>
            )}
          </>
        )}
        renderOption={(props, option, { selected }) => (
          <li {...props}>
            <Checkbox
              name="selectItem"
              type={CheckboxType.Alternative}
              isChecked={
                option.value === DEFAULT_SELECT_ALL_OPTION_VALUE
                  ? currentValues.length === selectOptions.length
                  : selected
              }
              onChange={() => {}}
            />
            {option.label}
          </li>
        )}
        renderInput={(param) => (
          <>
            <div className={styles.inputLabel}>{inputLabel}</div>
            <AutoCompleteInput isMobile={isMobile} errorMessage={errorMessage} {...param} />
          </>
        )}
        onChange={(_event, options) => {
          if (options.find((option) => option.value === DEFAULT_SELECT_ALL_OPTION_VALUE)) {
            onChange(currentValues.length === selectOptions.length ? [] : selectOptions);
            setCurrentValues(currentValues.length === selectOptions.length ? [] : selectOptions);
          } else {
            onChange(options);
            setCurrentValues(options);
          }
        }}
      />
      {!!errorMessage && <InputError errorMessage={errorMessage} />}
    </div>
  );
};

export default MultipleSelect;
